// src/components/StaffView.js
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import apiService from '../services/api'; // Direct import
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'; // Cell removed as not used
import { formatDate, formatScore, formatDateTime, getClientDisplayName } from '../utils';
import StaffEvaluationModal from './StaffEvaluationModal';
import { useAuth } from '../contexts/CognitoAuthContext';


const COLORS = ['#1f77b4', '#ff7f0e', '#2ca02c', '#d62728', '#9467bd', '#8c564b', '#e377c2', '#7f7f7f', '#bcbd22', '#17becf'];
const SCARF_COLOR_CURRENT = '#8884d8';
const SCARF_COLOR_TRAILING = 'rgba(136, 132, 216, 0.5)'; // More transparent
const EFFECTIVENESS_COLOR_CURRENT = '#82ca9d';
const EFFECTIVENESS_COLOR_TRAILING = 'rgba(130, 202, 157, 0.5)'; // More transparent
const STRUCTURE_COLOR_CURRENT = '#ffc658';
const STRUCTURE_COLOR_TRAILING = 'rgba(255, 198, 88, 0.5)'; // More transparent

// Helper to format staff evaluation report string (remains the same)
const formatStaffEvalToReportString = (staffEvalData, staffName) => {
    if (!staffEvalData) return `No stored report data found for ${staffName}.`;

    const formatList = (listItems) => {
        if (Array.isArray(listItems)) {
            return listItems.map(item => `* ${item}`).join('\n');
        }
        return listItems || 'N/A';
    };

    const periodStart = staffEvalData.period_start_date ? formatDate(staffEvalData.period_start_date) : null;
    const periodEnd = staffEvalData.period_end_date ? formatDate(staffEvalData.period_end_date) : null;
    const dateRangeStr = periodStart && periodEnd ? `${periodStart} to ${periodEnd}` : 'N/A';

    return `
# Long-Term Call Analysis for ${staffName}
(Report Generated: ${staffEvalData.report_generated_at ? formatDateTime(staffEvalData.report_generated_at) : 'N/A'};
Based on ${staffEvalData.call_eval_count || 'N/A'} call evaluations from ${dateRangeStr})

## Executive Summary
${staffEvalData.executive_summary || 'Not available.'}

---
## Trend Analysis
### SCARF Elements
* **Status:** ${staffEvalData.trend_analysis_scarf_status || 'N/A'}
* **Certainty:** ${staffEvalData.trend_analysis_scarf_certainty || 'N/A'}
* **Autonomy:** ${staffEvalData.trend_analysis_scarf_autonomy || 'N/A'}
* **Relatedness:** ${staffEvalData.trend_analysis_scarf_relatedness || 'N/A'}
* **Fairness:** ${staffEvalData.trend_analysis_scarf_fairness || 'N/A'}

### Communication Effectiveness Metrics
* **Empathy:** ${staffEvalData.trend_analysis_comm_empathy || 'N/A'}
* **Assertiveness:** ${staffEvalData.trend_analysis_comm_assertiveness || 'N/A'}
* **Respect:** ${staffEvalData.trend_analysis_comm_respect || 'N/A'}

### Linguistic Patterns
${staffEvalData.trend_analysis_ling_patterns || 'N/A'}

### Call Structure
${staffEvalData.trend_analysis_call_structure || 'N/A'}

---
## Detailed Assessment
${staffEvalData.detailed_assessment_scarf ? `### SCARF Assessment\n${staffEvalData.detailed_assessment_scarf}` : '### SCARF Assessment\nNot available.'}
${staffEvalData.detailed_assessment_comm ? `### Communication Effectiveness Metrics\n${staffEvalData.detailed_assessment_comm}` : '### Communication Effectiveness Metrics\nNot available.'}
${staffEvalData.detailed_assessment_ling ? `### Linguistic Pattern Analysis\n${staffEvalData.detailed_assessment_ling}` : '### Linguistic Pattern Analysis\nNot available.'}
${staffEvalData.detailed_assessment_struct ? `### Call Structure Assessment\n${staffEvalData.detailed_assessment_struct}` : '### Call Structure Assessment\nNot available.'}

---
## Chronological Trend Summary
${staffEvalData.chronological_trend_summary || 'Not available.'}

---
## Root-Cause Hypotheses
${formatList(staffEvalData.root_cause_hypotheses)}

---
## Specific Improvement Recommendations
* **Overall:** ${staffEvalData.recommendations_overall || 'N/A'}
* **Preparation:** ${staffEvalData.recommendations_preparation || 'N/A'}
* **Empathy:** ${staffEvalData.recommendations_empathy || 'N/A'}
* **Clarity & Certainty:** ${staffEvalData.recommendations_clarity_certainty || 'N/A'}
* **Structure:** ${staffEvalData.recommendations_structure || 'N/A'}
* **Cognitive Load:** ${staffEvalData.recommendations_cognitive_load || 'N/A'}

---
## Prioritized Action Plan
### Immediate (Next 1–2 Weeks)
* **Focus:** ${staffEvalData.action_plan_immediate_focus || 'N/A'}
* **Actions:**
${formatList(staffEvalData.action_plan_immediate_actions)}

### Short-Term (Next 3–4 Weeks)
* **Focus:** ${staffEvalData.action_plan_short_term_focus || 'N/A'}
* **Actions:**
${formatList(staffEvalData.action_plan_short_term_actions)}

### Medium-Term (Next 1–2 Months)
* **Focus:** ${staffEvalData.action_plan_medium_term_focus || 'N/A'}
* **Actions:**
${formatList(staffEvalData.action_plan_medium_term_actions)}
    `;
};


const StaffView = () => {
    const { staffId } = useParams();
    const { idToken, getIdTokenFromCookies } = useAuth(); // Get auth state from context
    
    const [staffDetails, setStaffDetails] = useState(null);
    const [keyMetrics, setKeyMetrics] = useState({ avgOverallCallScore: 0, totalCommunications: 0, pendingActionItems: 0 });
    // avgPerformanceScores will now hold both current and trailing
    const [avgPerformanceScores, setAvgPerformanceScores] = useState(null);
    const [communicationFrequency, setCommunicationFrequency] = useState([]);
    const [communicationClients, setCommunicationClients] = useState([]);
    const [report, setReport] = useState('');
    const [loadingReport, setLoadingReport] = useState(true);
    
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [isEvaluationModalOpen, setIsEvaluationModalOpen] = useState(false);

    const loadStaffData = useCallback(async () => {
        if (!apiService || !staffId) {
            setError("API service not available or Staff ID missing.");
            setLoading(false); setLoadingReport(false);
            return;
        }
        setLoading(true); setLoadingReport(true); setError(null); setReport('');

        let currentStaffName = 'this staff member';

        try {
            // Fetch staff profile directly by ID
            const staffProfile = await apiService.getStaffProfile(staffId);
            if (staffProfile) {
                setStaffDetails({ 
                    staffId: staffProfile.id, 
                    staffName: staffProfile.name, 
                    staffImageUrl: staffProfile.imageUrl,
                    department: staffProfile.department,
                    role: staffProfile.role,
                    emails: staffProfile.emails
                });
                currentStaffName = staffProfile.name;
            } else {
                // Fallback to list lookup
                const staffInfoResult = await apiService.getAllStaff();
                const currentStaff = staffInfoResult.find(s => s.staffId === staffId);
                setStaffDetails(currentStaff || { staffName: 'Unknown Staff', staffImageUrl: null });
                if (currentStaff) {
                    currentStaffName = currentStaff.staffName;
                }
            }
            
            const staffData = await apiService.getStaffViewData(staffId, null);

            setKeyMetrics(staffData.keyMetrics || { avgOverallCallScore: 0, totalCommunications: 0, pendingActionItems: 0 });
            setAvgPerformanceScores(staffData.avgPerformanceScores || {}); 
            
            const rawCommFreq = staffData.communicationFrequency;
            if (Array.isArray(rawCommFreq) && rawCommFreq.length > 0) {
                // Prefer displaying client names when available, avoiding raw UUIDs
                const uniqueClientsInFreq = [...new Set(
                    rawCommFreq.map(item => getClientDisplayName(item.forClientName, item.forClientId))
                )].sort();

                const clientWeeklyTotals = rawCommFreq.reduce((acc, item) => {
                    const week = formatDate(item.weekBeginning);
                    if (!week) return acc;

                    const clientKey = getClientDisplayName(item.forClientName, item.forClientId);
                    if (!clientKey) return acc;

                    if (!acc[week]) {
                        acc[week] = { weekBeginning: week };
                        uniqueClientsInFreq.forEach(client => {
                            acc[week][client] = 0;
                        });
                    }

                    acc[week][clientKey] = (acc[week][clientKey] || 0) + item.weeklyCount;
                    return acc;
                }, {});

                const chartData = Object.values(clientWeeklyTotals)
                    .sort((a, b) => new Date(a.weekBeginning) - new Date(b.weekBeginning));

                setCommunicationFrequency(chartData);
                setCommunicationClients(uniqueClientsInFreq);
            } else {
                setCommunicationFrequency([]);
                setCommunicationClients([]);
            }

            
            const rawReportData = staffData.staffEvalReport;
            if (rawReportData) {
                const formattedReport = formatStaffEvalToReportString(rawReportData, currentStaffName);
                setReport(formattedReport);
            } else {
                setReport(`No existing analysis report found for ${currentStaffName}.`);
            }

        } catch (err) {
            console.error("Error loading staff performance data:", err);
            setError("Failed to load staff performance data. " + err.message);
            setReport('');
        } finally {
            setLoading(false); setLoadingReport(false);
        }
    }, [staffId]);

    useEffect(() => {
        loadStaffData();
    }, [loadStaffData]);

    if (loading && loadingReport) {
        return <div className="flex items-center justify-center h-screen"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-800"></div><span className="ml-3">Loading staff performance...</span></div>;
    }

    if (error) {
        return <div className="p-8 bg-red-100 text-red-700 rounded-md"><h2>Error</h2><p>{error}</p><button onClick={loadStaffData} className="mt-4 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700">Retry</button></div>;
    }
    
    if (!staffDetails && !loading) {
        return <div className="p-8 text-center text-gray-500">Staff member not found.</div>
    }

    // Prepare data for charts with current and trailing scores
    const prepareChartData = (scorePrefix, names) => {
        if (!avgPerformanceScores) return [];
        return names.map(name => ({
            name: name.charAt(0).toUpperCase() + name.slice(1), // Capitalize
            currentScore: avgPerformanceScores[`current_avg_${scorePrefix}_${name}`],
            trailingScore: avgPerformanceScores[`trailing_avg_${scorePrefix}_${name}`],
        })).filter(item => (item.currentScore !== null && item.currentScore !== undefined) || (item.trailingScore !== null && item.trailingScore !== undefined));
    };

    const scarfScoreNames = ['status', 'certainty', 'autonomy', 'relatedness', 'fairness'];
    const effectivenessScoreNames = ['empathy', 'assertiveness', 'respect', 'clarity'];
    const callStructureScoreNames = ['opening', 'info_gathering', 'solution', 'closing']; // 'solution_presentation' becomes 'solution'

    const scarfScoresData = prepareChartData('scarf', scarfScoreNames);
    const effectivenessScoresData = prepareChartData('comm', effectivenessScoreNames); // 'comm' prefix in query for these
    const callStructureScoresData = prepareChartData('struct', callStructureScoreNames); // 'struct' prefix in query

    const overallScoreFromAvgCurrent = avgPerformanceScores?.current_avg_overall_score;
    const overallScoreFromAvgTrailing = avgPerformanceScores?.trailing_avg_overall_score;


    const handleTriggerEvaluation = async (staffId, staffName, startDate, endDate) => {
        let tokenToUse = idToken;
        
        if (!tokenToUse) {
            tokenToUse = await getIdTokenFromCookies();
        }
        
        if (!tokenToUse) {
            console.error('ID token is missing. User may need to log in again.');
            alert('Your session has expired. Please log out and log in again to use the staff evaluation feature.');
            return;
        }
        
        const result = await apiService.triggerStaffEvaluation(staffId, staffName, startDate, endDate, tokenToUse);
        // Reload the staff data to potentially get the new evaluation
        setTimeout(() => {
            loadStaffData();
        }, 5000); // Wait 5 seconds before reloading to give the evaluation time to process
        return result;
    };

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <div className="flex items-center mb-4 sm:mb-0">
                    {staffDetails?.staffImageUrl ? (
                        <img src={staffDetails.staffImageUrl} alt={staffDetails.staffName} className="w-16 h-16 rounded-full mr-4 object-cover shadow-md" 
                             onError={(e) => { e.target.onerror = null; e.target.src = `https://placehold.co/64x64/E2E8F0/718096?text=${staffDetails.staffName?.charAt(0) || 'S'}`; }}/>
                    ) : (
                         <div className="w-16 h-16 rounded-full mr-4 bg-gray-300 flex items-center justify-center text-gray-600 text-2xl font-semibold shadow-md">
                            {staffDetails?.staffName ? staffDetails.staffName.charAt(0) : 'S'}
                        </div>
                    )}
                    <div>
                        <h2 className="text-2xl md:text-3xl font-bold text-gray-800">{staffDetails?.staffName || 'Staff Member'}</h2>
                        <p className="text-gray-600">
                            Performance Overview (All Clients)
                        </p>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Avg. Overall Call Score (Current 14d)</h3>
                    <p className="text-2xl font-semibold text-gray-800">{overallScoreFromAvgCurrent?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Avg. Overall Call Score (Trailing 30d)</h3>
                    <p className="text-2xl font-semibold text-gray-800">{overallScoreFromAvgTrailing?.toFixed(2) || 'N/A'}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Total Communications</h3>
                    <p className="text-2xl font-semibold text-gray-800">{keyMetrics.totalCommunications || 0}</p>
                </div>
                <div className="bg-white p-4 rounded-lg shadow">
                    <h3 className="text-sm font-medium text-gray-500">Pending Action Items</h3>
                    <p className="text-2xl font-semibold text-gray-800">{keyMetrics.pendingActionItems || 0}</p>
                </div>
            </div>

            {/* Communication Frequency Chart */}
            <div className="bg-white p-4 rounded-lg shadow mb-6">
                <h3 className="text-lg font-semibold text-gray-700 mb-3">
                    Communication Frequency (Weekly, By Client)
                </h3>
                {communicationFrequency.length > 0 && communicationClients.length > 0 ? (
                    <ResponsiveContainer width="100%" height={300}>
                        <LineChart data={communicationFrequency}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="weekBeginning" tick={{ fontSize: 10 }} />
                            <YAxis allowDecimals={false} tick={{ fontSize: 10 }} />
                            <Tooltip wrapperClassName="text-xs"/>
                            <Legend />
                            {communicationClients.map((client, index) => (
                                <Line key={client} type="monotone" dataKey={client} name={`Client: ${client}`} stroke={COLORS[index % COLORS.length]} strokeWidth={2} connectNulls={true} />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                ) : <p className="text-gray-500 text-sm">No communication frequency data available for this staff member.</p>}
            </div>
            
            {/* Categorized Average Performance Scores Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
                {scarfScoresData.length > 0 && (
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-md font-semibold text-gray-700 mb-3">SCARF Model Scores</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={scarfScoresData} layout="vertical" margin={{ top: 5, right: 20, left: 50, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 9 }}/>
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={80} />
                                <Tooltip formatter={(value) => formatScore(value)} wrapperClassName="text-xs"/>
                                <Legend payload={[{ value: 'Current 30d', type: 'rect', color: SCARF_COLOR_CURRENT }, { value: 'Trailing 60d', type: 'rect', color: SCARF_COLOR_TRAILING }]}/>
                                <Bar dataKey="trailingScore" name="Trailing 60d" fill={SCARF_COLOR_TRAILING} />
                                <Bar dataKey="currentScore" name="Current 30d" fill={SCARF_COLOR_CURRENT} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {effectivenessScoresData.length > 0 && (
                     <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-md font-semibold text-gray-700 mb-3">Effectiveness Scores</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={effectivenessScoresData} layout="vertical" margin={{ top: 5, right: 20, left: 50, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 9 }} />
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={80}/>
                                <Tooltip formatter={(value) => formatScore(value)} wrapperClassName="text-xs"/>
                                <Legend payload={[{ value: 'Current 30d', type: 'rect', color: EFFECTIVENESS_COLOR_CURRENT }, { value: 'Trailing 60d', type: 'rect', color: EFFECTIVENESS_COLOR_TRAILING }]}/>
                                <Bar dataKey="trailingScore" name="Trailing 60d" fill={EFFECTIVENESS_COLOR_TRAILING} />
                                <Bar dataKey="currentScore" name="Current 30d" fill={EFFECTIVENESS_COLOR_CURRENT} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
                {callStructureScoresData.length > 0 && (
                    <div className="bg-white p-4 rounded-lg shadow">
                        <h3 className="text-md font-semibold text-gray-700 mb-3">Call Structure Scores</h3>
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={callStructureScoresData} layout="vertical" margin={{ top: 5, right: 20, left: 50, bottom: 5 }}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis type="number" domain={[0, 5]} ticks={[0,1,2,3,4,5]} tick={{ fontSize: 9 }}/>
                                <YAxis dataKey="name" type="category" tick={{ fontSize: 9 }} width={80}/>
                                <Tooltip formatter={(value) => formatScore(value)} wrapperClassName="text-xs"/>
                                <Legend payload={[{ value: 'Current 30d', type: 'rect', color: STRUCTURE_COLOR_CURRENT }, { value: 'Trailing 60d', type: 'rect', color: STRUCTURE_COLOR_TRAILING }]}/>
                                <Bar dataKey="trailingScore" name="Trailing 60d" fill={STRUCTURE_COLOR_TRAILING} />
                                <Bar dataKey="currentScore" name="Current 30d" fill={STRUCTURE_COLOR_CURRENT} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                )}
            </div>
            
            {(scarfScoresData.length === 0 && effectivenessScoresData.length === 0 && callStructureScoresData.length === 0 && avgPerformanceScores) &&
                 <div className="bg-white p-4 rounded-lg shadow mb-6">
                    <h3 className="text-lg font-semibold text-gray-700 mb-3">Average Performance Scores (Overall)</h3>
                    <p className="text-gray-500 text-sm">No detailed performance scores available for the selected period/client.</p>
                </div>
            }

            {/* Long-Term Analysis Report */}
            {loadingReport ? (
                <div className="flex items-center justify-center my-10">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-700"></div>
                    <p className="ml-3 text-gray-600">Loading report...</p>
                </div>
            ) : report ? (
                <div className="my-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4">
                        <h3 className="text-lg font-medium text-gray-900 mb-2 sm:mb-0">Long-Term Call Analysis Report (Global Context)</h3>
                        <button
                            onClick={() => setIsEvaluationModalOpen(true)}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none shadow-sm"
                        >
                            Generate Evaluation
                        </button>
                    </div>
                    <div className="bg-white shadow-lg rounded-lg p-6 border border-gray-200 prose prose-sm max-w-none">
                        <ReactMarkdown>{report}</ReactMarkdown>
                    </div>
                </div>
            ) : (
                 <div className="my-4 text-center text-sm text-gray-500 py-4">No report available.                </div>
            )}

            {/* Staff Evaluation Modal */}
            <StaffEvaluationModal
                isOpen={isEvaluationModalOpen}
                onClose={() => setIsEvaluationModalOpen(false)}
                staffId={staffId}
                staffName={staffDetails?.staffName || 'Staff Member'}
                onTriggerEvaluation={handleTriggerEvaluation}
            />
        </div>
    );
};

export default StaffView;